import * as React from 'react';
import { Control, Errors } from 'react-redux-form';
import { RouteComponentProps } from 'react-router';

import { PropertyManagerViewModel /*, PropertyManagementOfficeViewModel */ } from '@models/PropertyManager';
import { parsePhoneNumber } from '@common/parsers';
import { getDefaultErrorMessages, hasAlpha, hasValue, isValidEmail, isValidPhoneNumber } from '@common/validators';
import { Option } from 'react-select';

import { OfficeSearchViewModel, PageViewModel } from '@models/common.models';
import { Select, TypeAheadComponent, Breadcrumbs, CheckboxInline } from '@common/components';
import { DynamicSelectIdentifier } from '@common/components/dynamic-select/dynamic-select-types';
import { PropertyManagementOfficeViewModel } from '@models/PropertyManagementOffice';
import { FormWrapper } from '@common/components/form-wrapper/form-wrapper.component';

import { Role, Roles } from '@models/Roles';

// Defining the properties the component expects
// Consider this the "setup" of the component
// All properties should be readonly
export interface PropertyManagerComponentProperties extends RouteComponentProps<{ id?: number }> {
  readonly submit: (formData: PropertyManagerViewModel, isModal: boolean) => Promise<void>;
  readonly errorMessage: string;
  readonly id?: number;
  readonly isModal: boolean;
  readonly entityId?: number;
  readonly saving?: boolean;
  readonly getPropertyManagerInfo: (id?: number) => void;
  readonly officeLookup: (query: string, id?: number) => Promise<PageViewModel<PropertyManagementOfficeViewModel>>;
  readonly buttonRef: (button: HTMLButtonElement) => void;
  readonly clearForm: () => void;
  readonly loadedPropertyManager: PropertyManagerViewModel;
}

interface PropertyManagerComponentState {
  formDispatch?: Function;
}

// Component definition
export class PropertyManagerComponent extends React.Component<
  PropertyManagerComponentProperties,
  PropertyManagerComponentState
> {
  constructor(props: PropertyManagerComponentProperties) {
    super(props);
  }

  attachDispatch(dispatch: Function) {
    this.setState({
      formDispatch: dispatch
    });
  }

  componentDidMount() {
    if (this.props.match && this.props.match.params.id) {
      this.props.getPropertyManagerInfo(this.props.match.params.id);
    }
  }

  renderError() {
    if (!this.props.errorMessage) {
      return <div />;
    }
    return <div className="error">{this.props.errorMessage}</div>;
  }

  renderHideSave() {
    if (this.props.isModal) {
      return (
        <div>
          <button className="btn btn-hidden" type="submit" ref={this.props.buttonRef}>
            Save
          </button>
        </div>
      );
    }
    return (
      <div className="row text-right pull-right end-xs mt-10 mb-10">
        <button className="btn btn-secondary" type="button" onClick={() => window.history.back()}>
          Cancel
        </button>
        <button className="btn btn-primary" type="submit" disabled={this.props.saving} ref={this.props.buttonRef}>
          {this.props.saving ? 'Saving...' : 'Save'}
        </button>
      </div>
    );
  }

  renderOfficeSearch(suggestion: Option<OfficeSearchViewModel>) {
    return (
      <div>
        <div>Name: {suggestion.name ? suggestion.name : '—'} </div>
        <div>Phone #: {suggestion.phone ? suggestion.phone : '—'}</div>
        <div>Address: {suggestion.address && suggestion.address.line1 ? suggestion.address.line1 : '-'}</div>
        <div>City: {suggestion.address && suggestion.address.city ? suggestion.address.city : '-'}</div>
      </div>
    );
  }

  onSubmit(formData: PropertyManagerViewModel) {
    this.props.submit(formData, this.props.isModal);
  }

  render() {
    // if (this.props.match && this.props.match.params && this.props.match.params.id && this.props.isViewOnly) {
    //     return (
    //         <div>
    //             {<PropertyManagerViewOnlyComponent propertyManager={this.props.loadedPropertyManager} propertyManagementOffice={this.props.loadedPropertyManagementOffice} />
    //         </div>
    //     );
    // }

    return (
      <div>
        {this.props.isModal ? null : <Breadcrumbs />}
        <h2>Property Manager</h2>
        <FormWrapper
          shouldFormReset={true}
          model="propertyManagerForm"
          className="form-horizontal"
          onSubmit={(a: any) => this.onSubmit(a)}
          getDispatch={dispatch => this.attachDispatch(dispatch)}
          hideNativeErrors={true}
        >
          <Errors className="error error-pad" model="propertyManagerForm" show={true} />

          <div className="row">
            <div className="form-control col-xs-4">
              <label htmlFor="propertyManagerForm.name">Name</label>
              <Control.text model=".name" validators={{ hasAlpha: hasAlpha, required: hasValue }} maxLength={40} />
              <Errors
                className="error error-pad"
                model=".name"
                show={{ touched: true, focus: false }}
                messages={getDefaultErrorMessages()}
              />
            </div>
            <div className="form-control col-xs-4">
              <label htmlFor=".emailAddress">Email Address</label>
              <Control.text
                model="propertyManagerForm.emailAddress"
                validators={{ isValidEmail: isValidEmail, required: hasValue }}
                maxLength={50}
              />
              <Errors
                className="error error-pad"
                model=".emailAddress"
                show={{ touched: true, focus: false }}
                messages={getDefaultErrorMessages()}
              />
            </div>
            <div className="form-control col-xs-4">
              <label htmlFor=".propMgmtOffice.id">Property Management Office</label>
              <Control
                className="mt-10"
                model=".propMgmtOffice.id"
                component={(props: { value: ''; onChange: Function }) => {
                  return (
                    <TypeAheadComponent
                      {...props}
                      onChange={props.onChange}
                      renderValue={this.renderOfficeSearch}
                      valueKey={'id'}
                      labelKey={'name'}
                      getSuggestions={this.props.officeLookup}
                      value={props.value}
                    />
                  );
                }}
                validators={{ required: hasValue }}
              />
              <Errors
                className="error error-pad"
                model=".propMgmtOffice.id"
                show={{ touched: true, focus: false }}
                messages={getDefaultErrorMessages()}
              />
            </div>
          </div>

          <div className="row">
            <div className="form-control col-xs-4">
              <label htmlFor="propertyManagerForm.directPhone">Phone #</label>
              <Control.text
                model=".directPhone"
                parser={parsePhoneNumber}
                validators={{ required: hasValue, isValidPhoneNumber: isValidPhoneNumber }}
                maxLength={12}
              />
              <Errors
                className="error error-pad"
                model=".directPhone"
                show={{ touched: true, focus: false }}
                messages={getDefaultErrorMessages()}
              />
            </div>
            <div className="form-control col-xs-4">
              <label htmlFor="propertyManagerForm.directFax">Fax #</label>
              <Control.text
                model=".directFax"
                parser={parsePhoneNumber}
                validators={{ isFax: isValidPhoneNumber }}
                maxLength={12}
              />
              <Errors
                className="error error-pad"
                model=".directFax"
                show={{ touched: true, focus: false }}
                messages={getDefaultErrorMessages()}
              />
            </div>
          </div>
          <div className="row">
            <div className="form-control col-md-4">
              <label htmlFor="propertyManagerForm.propMgrType.id">Property Manager Type</label>
              <Select
                model=".propMgrType.id"
                identifier={DynamicSelectIdentifier.PropertyManagerTypes}
                validators={{ required: hasValue }}
              />
              <Errors
                className="error error-pad"
                model=".propMgrType.id"
                show={{ touched: true, focus: false }}
                messages={getDefaultErrorMessages()}
              />
            </div>
            <div className="form-control col-md-4">
              <label htmlFor="propertyManagerForm.PropertyManagerStatus.id">Property Manager Status</label>
              <Select
                model=".propMgrStatus.id"
                identifier={DynamicSelectIdentifier.PropertyManagerStatuses}
                validators={{ required: hasValue }}
                disabled={!Role.CheckRoles([Roles.Admin, Roles.IBSAdmin, Roles.IBSManager])}
              />
              <Errors
                className="error error-pad"
                model=".propMgrStatus.id"
                show={{ touched: true, focus: false }}
                messages={getDefaultErrorMessages()}
              />
            </div>
          </div>
          <div className="row mb-10 pb-10">
            <div className="col-md-3">
              <CheckboxInline>
                <Control.checkbox model=".onlineOrders" />
                <label className="checkbox-inline" htmlFor="propertyManagerForm.onlineOrders">
                  {' '}
                  Online Orders
                </label>
              </CheckboxInline>
            </div>
          </div>
          <div className="row mt-10">
            <div className="col-md-7">
              <label>Contact Preference</label>
              <div className="row">
                <div className="col-md-3">
                  <CheckboxInline>
                    <Control.checkbox model=".usps" />
                    <label className="checkbox-inline" htmlFor="propertyManagerForm.usps">
                      USPS
                    </label>
                  </CheckboxInline>
                </div>
                <div className="col-md-3">
                  <CheckboxInline>
                    <Control.checkbox model=".fax" />
                    <label className="checkbox-inline" htmlFor="propertyManagerForm.fax">
                      {' '}
                      Fax
                    </label>
                  </CheckboxInline>
                </div>
                <div className="col-md-3">
                  <CheckboxInline>
                    <Control.checkbox model=".email" />
                    <label className="checkbox-inline" htmlFor="propertyManagerForm.email">
                      {' '}
                      Email
                    </label>
                  </CheckboxInline>
                </div>
              </div>
            </div>
          </div>
          {this.renderHideSave()}
        </FormWrapper>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/property-manager/_components/property-manager/property-manager.component.tsx